import { Home, Building2, Mail, PenLine, Scale, ShieldAlert } from "lucide-react";
import {
  LegalPage,
  LegalSection,
  LegalHeading,
} from "@/components/blocks/LegalPage";
import { COMPANY, HAS_VAT_ID, VAT_ID } from "@/lib/legal";
import { CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_HREF } from "@/lib/brand";

const SIDEBAR_ITEMS = [
  { id: "top", label: "Start", icon: Home },
  { id: "anbieter", label: "Anbieter", icon: Building2 },
  { id: "kontakt", label: "Kontakt", icon: Mail },
  { id: "verantwortlich", label: "Verantwortlich", icon: PenLine },
  { id: "streit", label: "Streitbeilegung", icon: Scale },
  { id: "haftung", label: "Haftung", icon: ShieldAlert },
];

const linkClass =
  "text-white/80 underline decoration-white/25 underline-offset-4 hover:text-white hover:decoration-brand-orange transition-colors duration-300";

export default function Imprint() {
  return (
    <LegalPage
      eyebrow="Rechtliches"
      title="Impressum"
      lead="Angaben gemäß § 5 Digitale-Dienste-Gesetz (DDG) und § 18 Absatz 2 Medienstaatsvertrag (MStV)."
      sidebarItems={SIDEBAR_ITEMS}
    >
      <LegalSection id="anbieter" title="Angaben gemäß § 5 DDG">
        <p>
          MatchGate ist ein Produkt von NxGn Media. Betreiberin dieses Angebots
          ist:
        </p>
        <p>
          {COMPANY.name}
          <br />
          {COMPANY.street}
          <br />
          {COMPANY.postalCode} {COMPANY.city}
          <br />
          {COMPANY.country}
        </p>

        <LegalHeading>Vertreten durch</LegalHeading>
        <p>{COMPANY.representedBy}</p>

        {HAS_VAT_ID && (
          <>
            <LegalHeading>Umsatzsteuer-Identifikationsnummer</LegalHeading>
            <p>Gemäß § 27a Umsatzsteuergesetz: {VAT_ID}</p>
          </>
        )}
      </LegalSection>

      <LegalSection id="kontakt" title="Kontakt">
        <p>
          Telefon:{" "}
          <a href={CONTACT_PHONE_HREF} className={linkClass}>
            {CONTACT_PHONE}
          </a>
          <br />
          E-Mail:{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className={linkClass}>
            {CONTACT_EMAIL}
          </a>
        </p>
      </LegalSection>

      <LegalSection id="verantwortlich" title="Redaktionell verantwortlich">
        <p>
          Verantwortlich für den Inhalt nach § 18 Absatz 2 MStV:
          <br />
          {COMPANY.editorialResponsible.name}
          <br />
          {COMPANY.editorialResponsible.street}
          <br />
          {COMPANY.editorialResponsible.city}
        </p>
      </LegalSection>

      <LegalSection id="streit" title="Verbraucherstreitbeilegung">
        <p>
          Wir sind nicht bereit und nicht verpflichtet, an
          Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle
          teilzunehmen.
        </p>
        <p>
          Hinweis: Die Online-Streitbeilegungsplattform der Europäischen
          Kommission wurde zum 20. Juli 2025 eingestellt. Ein Verweis darauf ist
          daher nicht mehr erforderlich.
        </p>
      </LegalSection>

      <LegalSection id="haftung" title="Haftung und Urheberrecht">
        <LegalHeading>Haftung für Inhalte</LegalHeading>
        <p>
          Als Diensteanbieter sind wir gemäß § 7 Absatz 1 DDG für eigene Inhalte
          auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach
          §§ 8 bis 10 DDG sind wir als Diensteanbieter jedoch nicht
          verpflichtet, übermittelte oder gespeicherte fremde Informationen zu
          überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige
          Tätigkeit hinweisen.
        </p>
        <p>
          Verpflichtungen zur Entfernung oder Sperrung der Nutzung von
          Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt.
          Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der
          Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden
          entsprechender Rechtsverletzungen entfernen wir diese Inhalte
          umgehend.
        </p>

        <LegalHeading>Haftung für Links</LegalHeading>
        <p>
          Unser Angebot enthält Links zu externen Websites Dritter, auf deren
          Inhalte wir keinen Einfluss haben. Deshalb können wir für diese
          fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der
          verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der
          Seiten verantwortlich.
        </p>
        <p>
          Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche
          Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt
          der Verlinkung nicht erkennbar. Eine permanente inhaltliche Kontrolle
          der verlinkten Seiten ist ohne konkrete Anhaltspunkte einer
          Rechtsverletzung jedoch nicht zumutbar. Bei Bekanntwerden von
          Rechtsverletzungen entfernen wir derartige Links umgehend.
        </p>

        <LegalHeading>Urheberrecht</LegalHeading>
        <p>
          Die durch uns erstellten Inhalte und Werke auf diesen Seiten
          unterliegen dem deutschen Urheberrecht. Die Vervielfältigung,
          Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der
          Grenzen des Urheberrechts bedürfen unserer schriftlichen Zustimmung.
        </p>
        <p>
          Soweit die Inhalte auf dieser Seite nicht von uns erstellt wurden,
          werden die Urheberrechte Dritter beachtet. Insbesondere werden Inhalte
          Dritter als solche gekennzeichnet. Sollten Sie dennoch auf eine
          Urheberrechtsverletzung aufmerksam werden, bitten wir um einen
          entsprechenden Hinweis. Bei Bekanntwerden von Rechtsverletzungen
          entfernen wir derartige Inhalte umgehend.
        </p>

        <LegalHeading>Bildnachweise</LegalHeading>
        <p>
          Die auf dieser Website verwendeten Beispielbilder stammen von Unsplash
          (unsplash.com) und werden unter der Unsplash-Lizenz verwendet.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
